import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createRoutingRule,
  listPolicies,
  listRoutingRules,
  resetSitePolicy,
  savePolicy,
  updateRoutingRule,
  type PolicyInput,
  type RoutingRole,
  type RoutingRule,
  type RoutingRuleInput,
  type SlaPolicyRow,
} from '../api';
import type { FeedbackLocale, FeedbackTranslate } from '../i18n';
import {
  Button,
  Card,
  DataTable,
  Drawer,
  Field,
  Notice,
  Select,
  StatusBadge,
  TextInput,
  type Column,
} from '../../../shell/kit';
import { useResource } from '../../../shell/hooks';
import { sites as loadSites } from '../../../api';
import { EmployeePicker } from '../../../shell/pickers';
import { useToast } from '../../../shell/toast';
import { errorText } from '../../../shell/errors';
import { useCategoryCatalog } from '../catalog';

const ROLES: readonly RoutingRole[] = ['site_manager', 'marketer', 'network_admin'];
const CHANNELS = ['qr', 'whatsapp', 'app', 'phone', 'import'] as const;

const DEFAULTS: PolicyInput = {
  siteId: null,
  responseMinutes: 1440,
  remindBeforeMinutes: 120,
  lowScoreThreshold: 3,
  visitWindowMinutes: 240,
  publicWindowMinutes: 10,
  publicMaxPerWindow: 5,
};

/** Незаполненный выбор сотрудника — это «не задано», а не пустой идентификатор. */
function clean(input: RoutingRuleInput): RoutingRuleInput {
  return {
    ...input,
    name: input.name.trim(),
    assigneeId: input.assigneeId || null,
    escalateToId: input.escalateToId || null,
  };
}

const NUMERIC_FIELDS: ReadonlyArray<[keyof Omit<PolicyInput, 'siteId'>, string]> = [
  ['responseMinutes', 'feedback.set.response'],
  ['remindBeforeMinutes', 'feedback.set.remind'],
  ['lowScoreThreshold', 'feedback.set.lowScore'],
  ['visitWindowMinutes', 'feedback.set.visitWindow'],
  ['publicWindowMinutes', 'feedback.set.publicWindow'],
  ['publicMaxPerWindow', 'feedback.set.publicMax'],
];

/**
 * Срок ответа и маршрутизация (М5.3). Раньше оба справочника менялись только
 * прямым SQL; здесь — политика сети, особые сроки объектов и правила, какое
 * обращение кому уходит и кому эскалируется.
 */
export function SettingsView({
  t,
  locale,
  canEdit,
}: {
  t: FeedbackTranslate;
  locale: FeedbackLocale;
  canEdit: boolean;
}) {
  const toast = useToast();
  const siteList = useResource(() => loadSites(), []);
  const { catalog, categoryName } = useCategoryCatalog();
  const [policies, setPolicies] = useState<readonly SlaPolicyRow[]>([]);
  const [rules, setRules] = useState<readonly RoutingRule[]>([]);
  const [scope, setScope] = useState('');
  const [draft, setDraft] = useState<PolicyInput>(DEFAULTS);
  const [editing, setEditing] = useState<{ id: string | null; input: RoutingRuleInput } | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = useCallback(
    (err: unknown) => {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    },
    [toast],
  );

  const load = useCallback(async () => {
    try {
      setPolicies((await listPolicies()).items);
      setRules((await listRoutingRules()).items);
    } catch (err) {
      fail(err);
    }
  }, [fail]);

  useEffect(() => {
    void load();
  }, [load]);

  // Выбранная область подставляет в форму свою политику или, если её нет, политику сети.
  useEffect(() => {
    const own = policies.find((p) => (p.siteId ?? '') === scope);
    const network = policies.find((p) => p.siteId === null);
    const base = own ?? network;
    setDraft(base ? { ...base, siteId: scope || null } : { ...DEFAULTS, siteId: scope || null });
  }, [scope, policies]);

  const overridden = useMemo(() => new Set(policies.filter((p) => p.siteId).map((p) => p.siteId as string)), [policies]);

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      toast.ok(t('feedback.common.saved'));
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const roleOrEmployee = (role: RoutingRole | null, name: string | null): string =>
    name ?? (role ? `${t(`feedback.role.${role}` as never)} (${t('feedback.set.ruleByRole')})` : '—');

  const ruleColumns: ReadonlyArray<Column<RoutingRule>> = [
    { key: 'p', header: t('feedback.set.rulePriority'), numeric: true, render: (r) => r.priority },
    {
      key: 'n',
      header: t('feedback.set.ruleName'),
      render: (r) => (
        <span className="flex items-center gap-2">
          {r.name}
          {r.status === 'archived' ? <StatusBadge>{t('feedback.set.ruleArchived')}</StatusBadge> : null}
        </span>
      ),
    },
    { key: 's', header: t('feedback.set.ruleSite'), render: (r) => r.siteName ?? t('feedback.set.ruleAnySite') },
    {
      key: 'c',
      header: t('feedback.set.ruleCategory'),
      render: (r) =>
        [
          r.categoryCode ? categoryName(r.categoryCode) : t('feedback.set.ruleAny'),
          r.scoreMax ? `≤ ${r.scoreMax}` : null,
          r.channel ? t(`feedback.channel.${r.channel}` as never) : null,
        ]
          .filter(Boolean)
          .join(' · '),
    },
    { key: 'a', header: t('feedback.set.ruleAssignee'), render: (r) => roleOrEmployee(r.assigneeRole, r.assigneeName) },
    { key: 'e', header: t('feedback.set.ruleEscalate'), render: (r) => roleOrEmployee(r.escalateToRole, r.escalateToName) },
    { key: 'm', header: t('feedback.set.ruleResponse'), numeric: true, render: (r) => r.responseMinutes ?? '—' },
    {
      key: 'x',
      header: '',
      render: (r) =>
        canEdit ? (
          <Button
            size="sm"
            onClick={() =>
              setEditing({
                id: r.id,
                input: {
                  name: r.name,
                  siteId: r.siteId,
                  categoryCode: r.categoryCode,
                  scoreMax: r.scoreMax,
                  channel: r.channel,
                  assigneeId: r.assigneeId,
                  assigneeRole: r.assigneeRole,
                  escalateToId: r.escalateToId,
                  escalateToRole: r.escalateToRole,
                  responseMinutes: r.responseMinutes,
                  priority: r.priority,
                  status: r.status,
                },
              })
            }
          >
            {t('feedback.queue.open')}
          </Button>
        ) : null,
    },
  ];

  const sites = siteList.data ?? [];

  return (
    <div className="space-y-4">
      <Card title={t('feedback.set.policy')} hint={t('feedback.set.policyHint')}>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label={t('feedback.set.scope')}>
            <Select value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="">{t('feedback.set.network')}</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {overridden.has(s.id) ? ` — ${t('feedback.set.override')}` : ''}
                </option>
              ))}
            </Select>
          </Field>
          {NUMERIC_FIELDS.map(([key, label]) => (
            <Field key={key} label={t(label as never)}>
              <TextInput
                type="number"
                min={key === 'remindBeforeMinutes' ? 0 : 1}
                value={String(draft[key])}
                disabled={!canEdit}
                onChange={(e) => setDraft({ ...draft, [key]: Number(e.target.value) })}
              />
            </Field>
          ))}
        </div>
        {canEdit ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button tone="primary" disabled={busy} onClick={() => void run(() => savePolicy(draft))}>
              {t('feedback.set.save')}
            </Button>
            {scope && overridden.has(scope) ? (
              <Button disabled={busy} onClick={() => void run(() => resetSitePolicy(scope))}>
                {t('feedback.set.reset')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </Card>

      <Card
        title={t('feedback.set.rules')}
        hint={t('feedback.set.rulesHint')}
        actions={
          canEdit ? (
            <Button
              tone="primary"
              onClick={() =>
                setEditing({
                  id: null,
                  input: {
                    name: '',
                    siteId: null,
                    categoryCode: null,
                    scoreMax: null,
                    channel: null,
                    assigneeId: null,
                    assigneeRole: 'site_manager',
                    escalateToId: null,
                    escalateToRole: 'network_admin',
                    responseMinutes: null,
                    priority: 100,
                    status: 'active',
                  },
                })
              }
            >
              {t('feedback.set.ruleNew')}
            </Button>
          ) : null
        }
      >
        <DataTable rows={rules} columns={ruleColumns} empty={t('feedback.common.nothing')} rowKey={(r) => r.id} />
      </Card>

      {editing ? (
        <Drawer title={t('feedback.set.ruleEdit')} onClose={() => setEditing(null)}>
          <RuleForm
            t={t}
            locale={locale}
            sites={sites}
            categories={catalog?.categories ?? []}
            value={editing.input}
            onChange={(input) => setEditing({ ...editing, input })}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              tone="primary"
              disabled={busy || editing.input.name.trim().length === 0}
              onClick={() =>
                void run(async () => {
                  if (editing.id) await updateRoutingRule(editing.id, clean(editing.input));
                  else await createRoutingRule(clean(editing.input));
                  setEditing(null);
                })
              }
            >
              {t('feedback.set.save')}
            </Button>
            {editing.id ? (
              <Button
                tone={editing.input.status === 'active' ? 'danger' : 'ghost'}
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await updateRoutingRule(editing.id as string, {
                      ...clean(editing.input),
                      status: editing.input.status === 'active' ? 'archived' : 'active',
                    });
                    setEditing(null);
                  })
                }
              >
                {editing.input.status === 'active' ? t('feedback.set.ruleArchive') : t('feedback.set.ruleRestore')}
              </Button>
            ) : null}
          </div>
          <div className="mt-3">
            <Notice tone="info">{t('feedback.set.rulesHint')}</Notice>
          </div>
        </Drawer>
      ) : null}
    </div>
  );
}

function RuleForm({
  t,
  locale,
  sites,
  categories,
  value,
  onChange,
}: {
  t: FeedbackTranslate;
  locale: FeedbackLocale;
  sites: ReadonlyArray<{ id: string; name: string }>;
  categories: ReadonlyArray<{ code: string; nameRu: string; nameKk: string }>;
  value: RoutingRuleInput;
  onChange: (next: RoutingRuleInput) => void;
}) {
  const set = (patch: Partial<RoutingRuleInput>): void => onChange({ ...value, ...patch });
  const target = (kind: 'assignee' | 'escalate') => {
    const roleKey = kind === 'assignee' ? 'assigneeRole' : 'escalateToRole';
    const idKey = kind === 'assignee' ? 'assigneeId' : 'escalateToId';
    const byRole = value[idKey] === null;
    return (
      <Field label={t(kind === 'assignee' ? 'feedback.set.ruleAssignee' : 'feedback.set.ruleEscalate')}>
        <div className="flex flex-col gap-2">
          <Select
            value={byRole ? `role:${value[roleKey] ?? ''}` : 'employee'}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'employee') set({ [roleKey]: null, [idKey]: '' } as Partial<RoutingRuleInput>);
              else set({ [roleKey]: (v.slice(5) || null) as RoutingRole | null, [idKey]: null } as Partial<RoutingRuleInput>);
            }}
          >
            {kind === 'escalate' ? <option value="role:">—</option> : null}
            {ROLES.map((r) => (
              <option key={r} value={`role:${r}`}>
                {t(`feedback.role.${r}` as never)} ({t('feedback.set.ruleByRole')})
              </option>
            ))}
            <option value="employee">{t('feedback.set.ruleEmployee')}</option>
          </Select>
          {!byRole ? (
            <EmployeePicker
              value={value[idKey] ?? ''}
              onChange={(id) => set({ [idKey]: id || '' } as Partial<RoutingRuleInput>)}
              siteId={value.siteId}
              emptyLabel={t('feedback.action.assigneeEmpty')}
            />
          ) : null}
        </div>
      </Field>
    );
  };

  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Field label={t('feedback.set.ruleName')} required>
        <TextInput value={value.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} />
      </Field>
      <Field label={t('feedback.set.rulePriority')}>
        <TextInput type="number" min={0} value={String(value.priority)} onChange={(e) => set({ priority: Number(e.target.value) })} />
      </Field>
      <Field label={t('feedback.set.ruleSite')}>
        <Select value={value.siteId ?? ''} onChange={(e) => set({ siteId: e.target.value || null })}>
          <option value="">{t('feedback.set.ruleAnySite')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('feedback.set.ruleCategory')}>
        <Select value={value.categoryCode ?? ''} onChange={(e) => set({ categoryCode: e.target.value || null })}>
          <option value="">{t('feedback.set.ruleAny')}</option>
          {categories.map((c) => (
            <option key={c.code} value={c.code}>
              {locale === 'kk' ? c.nameKk : c.nameRu}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('feedback.set.ruleScore')}>
        <Select value={value.scoreMax === null ? '' : String(value.scoreMax)} onChange={(e) => set({ scoreMax: e.target.value ? Number(e.target.value) : null })}>
          <option value="">{t('feedback.set.ruleAny')}</option>
          {[1, 2, 3, 4, 5].map((s) => (
            <option key={s} value={String(s)}>
              ≤ {s}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('feedback.set.ruleChannel')}>
        <Select value={value.channel ?? ''} onChange={(e) => set({ channel: e.target.value || null })}>
          <option value="">{t('feedback.set.ruleAny')}</option>
          {CHANNELS.map((c) => (
            <option key={c} value={c}>
              {t(`feedback.channel.${c}` as never)}
            </option>
          ))}
        </Select>
      </Field>
      {target('assignee')}
      {target('escalate')}
      <Field label={t('feedback.set.ruleResponse')}>
        <TextInput
          type="number"
          min={5}
          value={value.responseMinutes === null ? '' : String(value.responseMinutes)}
          onChange={(e) => set({ responseMinutes: e.target.value ? Number(e.target.value) : null })}
        />
      </Field>
    </div>
  );
}
